import { useEffect, useState } from 'react';
import axios from 'axios';
import Main from '../../../component/Main';
import SearchBtn from '../../../component/common/button/SearchBtn';
import NewDataGrid from '../../../component/common/dataGrid/NewDataGrid';
import useModalStore from '../../../zustand/store/useModalStore';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import useMenuPermission from '../../../hooks/useMenuPermission';
import * as Icons from '../../../images/icons/icons_component';
import RoleDetail from './RoleDetail';
import RoleAdd_layer from './RoleAdd_layer';
import { ROLE_API, handleRoleApiError, roleGridPropsInfo, roleSearchParamsInit, roleSelectCellParams } from './roleMngApi';

import '../../../css/role-mng.css';

/**
 * 롤권한관리 (/manager/role_mng.do)
 * 롤 목록 조회 · 추가(모달) · 상세 수정
 */
const RoleMng = () => {

    const [searchParams, setSearchParams] = useState(roleSearchParamsInit);
    const searchParamsAdditional = {};

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    const [state, setState] = useState({
        dataList: [],
        isSearched: false,
        isLoading: false,
    });

    const [pageInfo, setPageInfo] = useState({
        paginationTF: false,
        currentPage: 1,
        itemsPerPage: 50,
        totalItems: 0,
    });

    // 데이터 등록/수정 후 re search
    const [reSearch, setReSearch] = useState(false);

    const showNotiModal = useModalStore(state => state.showNotiModal);

    // 권한코드 목록 (검색조건 + 추가 모달 공용)
    const [authList, setAuthList] = useState([]);
    useEffect(() => {
        const fetchAuthList = async () => {
            try {
                const response = await axios.get(ROLE_API.authCodes);
                setAuthList(response.data.authList ?? []);
            } catch (error) {
                handleRoleApiError(error, showNotiModal, '권한코드 목록 조회 중 오류가 발생했습니다.');
            }
        };
        fetchAuthList();
    }, [showNotiModal]);

    // 그리드 행 선택 → 상세 조회
    const [selectRole, setSelectRole] = useState({ roleno: '' });
    const [selectRow, setSelectRow] = useState();
    const [detail, setDetail] = useState(null);

    const handleOnClick = (dataList, index, selectCellParams, key) => {
        setSelectRole({ roleno: dataList[index][selectCellParams[0]] });
        setSelectRow(key);
    };

    useEffect(() => {
        if (selectRole.roleno === '') return;
        const fetchDetail = async () => {
            try {
                const response = await axios.get(ROLE_API.detail, { params: { roleno: selectRole.roleno } });
                setDetail(response.data.roleDetail ?? null);
            } catch (error) {
                handleRoleApiError(error, showNotiModal, '롤 상세정보 조회 중 오류가 발생했습니다.');
            }
        };
        fetchDetail();
    }, [selectRole, showNotiModal]);

    // 등록/수정 후 목록 재조회 + 상세 갱신
    const refreshAfterSave = (roleno) => {
        setReSearch(true);
        setSelectRole({ roleno, refresh: Date.now() });
    };

    // 삭제 후 목록 재조회 + 상세 비움
    const refreshAfterDelete = () => {
        setReSearch(true);
        setSelectRole({ roleno: '' });
        setSelectRow(undefined);
        setDetail(null);
    };

    // 롤 추가 모달
    const [addOpen, setAddOpen] = useState(false);

    const activeEnter = (e) => {
        if (e.key === 'Enter') setReSearch(true);
    };

    const { primaryBackColor } = useDynamicStyle();
    // 롤권한관리 메뉴 권한(롤 권한 + 개인 권한). 버튼은 그대로 두고 권한이 없으면 누를 때 안내한다(서버도 같은 기준으로 막는다)
    const { withPermission } = useMenuPermission();

    return (
        <Main>
            {/* 롤 추가 modal */}
            <RoleAdd_layer
                open={addOpen}
                setOpen={setAddOpen}
                authList={authList}
                onSaved={refreshAfterSave}
            />
            <div className='h-full flex flex-col min-h-0 flex-grow'>
                <div className='com-search'>
                    <table>
                        <colgroup>
                            <col width='12%' />
                            <col width='21%' />
                            <col width='12%' />
                            <col width='21%' />
                            <col width='12%' />
                            <col width='22%' />
                        </colgroup>
                        <tbody>
                            <tr>
                                <th><label htmlFor='role-search-authjobs'>권한코드</label></th>
                                <td>
                                    <select id='role-search-authjobs' className='w-full' name='searchAuthJobs' value={searchParams.searchAuthJobs} onChange={handleInputChange}>
                                        <option value=''>==전체==</option>
                                        {authList.map((item) => (
                                            <option key={item.bscdminr} value={item.bscdminr}>{item.bscdminr} - {item.bscddesc}</option>
                                        ))}
                                    </select>
                                </td>
                                <th><label htmlFor='role-search-rolename'>롤명</label></th>
                                <td>
                                    <input id='role-search-rolename' type='text' className='input_style' name='searchRoleName'
                                        value={searchParams.searchRoleName} onChange={handleInputChange} onKeyDown={activeEnter} />
                                </td>
                                <th><label htmlFor='role-search-useyn'>사용여부</label></th>
                                <td>
                                    <select id='role-search-useyn' className='w-full' name='searchUseYn' value={searchParams.searchUseYn} onChange={handleInputChange}>
                                        <option value=''>==전체==</option>
                                        <option value='Y'>사용</option>
                                        <option value='N'>미사용</option>
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <SearchBtn
                    propsInfo={roleGridPropsInfo}
                    state={state}
                    setState={setState}
                    searchParams={searchParams}
                    searchParamsAdditional={searchParamsAdditional}
                    pageInfo={pageInfo}
                    setPageInfo={setPageInfo}
                    reSearch={reSearch}
                    setReSearch={setReSearch}
                >
                    <button className={`search_btn ${primaryBackColor}`} onClick={withPermission('UPYN', () => setAddOpen(true))} title='롤 추가'>
                        <Icons.AddCircle16w />추가
                    </button>
                </SearchBtn>
                <div className='flex flex-col min-h-0 flex-grow overflow-auto pr-1'>
                    <div className='role_split_fill'>
                        <div className='mb-2 bold'>■ 롤 목록</div>
                        <div className='flex overflow-hidden flex-grow mng_data_grid'>
                            <NewDataGrid
                                propsInfo={roleGridPropsInfo}
                                state={state}
                                pageInfo={pageInfo}
                                setPageInfo={setPageInfo}
                                selectCellParams={roleSelectCellParams}
                                handleOnClick={handleOnClick}
                                selectRow={selectRow}
                            />
                        </div>
                    </div>
                    <div className='role_split_auto'>
                        <div className='mb-2 bold'>■ 롤 상세정보</div>
                        <RoleDetail
                            detail={detail}
                            onSaved={refreshAfterSave}
                            onDeleted={refreshAfterDelete}
                        />
                    </div>
                </div>
            </div>
        </Main>
    );
};

export default RoleMng;
